import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { trocarParametroNaUrl } from '../config/rotas';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  createColumnHelper,
  FilterFn,
} from '@tanstack/react-table';
import { fmtCNPJ, fmtDate, fmtDateTime } from '../utils/formatters';
import { leadsService } from '../services/api';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm } from './shared/ConfirmDialog';
import { confirmacaoDeExcluirLead, O_QUE_A_ETAPA_FAZ, casaEscopo, EscopoDoResponsavel, vistoDoLead, payloadDaAnotacao, fraseDaRecusaDaAnotacao, EtapasDoFunil, ETAPAS_FIXAS, temProximaAcao, atrasoDaProximaAcao } from '../utils/crm';
import { tomDoAtraso } from '../utils/recebiveis';
import IconeDoHub from './shared/Icon';
import { lerImpactoDaExclusao } from '../utils/impactoDaExclusaoDoLead';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, Carga, acaoPrincipal, acaoSecundaria, campo, campoComEstado } from '../design';

const avatarColors = [
  'bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500',
  'bg-indigo-500', 'bg-teal-500',
];
const getAvatarColor = (id: number) => avatarColors[(id || 0) % avatarColors.length];

/**
 * A CASCA DA CENTRAL (Fase 2, tela 1, 08/10/2026): os tokens `central-*` do tailwind.config (guia §5c e §14). Na
 * Central o botão não é preenchido: a ação é FANTASMA (fundo da superfície, borda 1px) e a destrutiva é CONTORNADA
 * (transparente, borda e texto no vermelho). Estado é peso + ícone + barra, nunca cor no texto.
 */
const BOTAO_FANTASMA = 'bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado';
const BOTAO_PERIGO = 'bg-transparent border border-central-destrutivo text-central-destrutivo hover:bg-central-destrutivo/10';
const SUPERFICIE = 'bg-central-cartao border border-central-borda rounded-central';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

type TabKey = 'pipeline' | 'won' | 'lost' | 'frozen' | 'archived';
/**
 * A ABA NA URL (15/10/2026, estado na URL, PR 5): "?aba=ganhos|perdidos|congelados|arquivados", com replace. O Pipeline
 * (o padrão) deixa a URL limpa; valor desconhecido abre o Pipeline.
 */
const ABA_NA_URL: Record<Exclude<TabKey, 'pipeline'>, string> = { won: 'ganhos', lost: 'perdidos', frozen: 'congelados', archived: 'arquivados' };
const abaDaUrl = (search: string): TabKey => {
  const v = new URLSearchParams(search).get('aba');
  const achada = (Object.keys(ABA_NA_URL) as Array<keyof typeof ABA_NA_URL>).find((k) => ABA_NA_URL[k] === v);
  return achada ?? 'pipeline';
};

interface LeadsBoardProps {
  /**
   * O termo da busca global (barra do topo) ou do "ver DDRs" do cliente (15/10/2026): a tela abre já filtrada por ele.
   * Vinha para o `search` do App, que esta tela não lê (a busca dela é própria). Mesmo padrão de Faturas e Despesas.
   */
  initialSearch?: string;
  onSearchConsumed?: () => void;
  leads: any[];
  canEdit: boolean;
  loading: boolean;
  /**
   * O estado da carga dos leads (a carga principal do App). Sem ela a tela não sabe a diferença entre "não há leads"
   * e "os leads não chegaram": com a API fora, mostrava o funil vazio, os KPIs em R$ 0,00 e o botão de criar lead.
   */
  carga: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm'>;
  onEdit: (lead: any) => void;
  onStatusChange: (id: number, status: string, lostReason?: string) => void;
  onLoadData: () => void;
  onLeadUpdate: (lead: any) => void;
  onLeadRemove: (id: number) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
  Icon: React.FC<{ name: string; className?: string }>;
  /** B6: as propostas que o App já carrega na carga principal; daqui sai o visto/não visto do card. */
  propostas?: any[];
  /** A4: as etapas do funil (do servidor, ou as seis fixas). Sem a prop, as fixas. */
  funil?: EtapasDoFunil;
  /** B1 (#411): concluir a próxima ação pelo card. */
  onConcluirProximaAcao?: (lead: any) => void;
}

/**
 * Corpo rolável de uma coluna do Kanban.
 *
 * ⛔ O scroll da coluna era MUDO. A área útil dá ~9 cards compactos; "Proposta"
 * tem 11. O 10º ficava cortado ao meio, o 11º invisível, e não havia NADA
 * dizendo isso: a scrollbar do macOS some quando ociosa, não há sombra, não há
 * borda. O badge do cabeçalho dizia "11" e a tela mostrava 9 — quem não rolasse
 * por acidente concluía que o lead tinha sumido.
 *
 * Pior no pipeline do que numa lista qualquer: o que fica escondido no fundo é
 * exatamente o que a ordenação empurrou para lá (menor valor, menos parado),
 * então o corte é silencioso E sistemático.
 *
 * Agora a borda de baixo escurece e uma pílula diz quantos faltam; clicar rola.
 * Tudo some sozinho quando a lista cabe — coluna curta continua limpa.
 */
function ColunaRolavel({
  className,
  recalcularEm,
  arrastando,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: {
  className: string;
  /** Muda quando a lista muda — dispara remedição. */
  recalcularEm: string;
  /** Durante o drag a pílula some: botão sobre a zona de drop atrapalharia o drop. */
  arrastando: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const quadroRef = useRef(0);
  const [ocultos, setOcultos] = useState(0);
  const [rolado, setRolado] = useState(false);

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const limite = el.getBoundingClientRect().bottom;
    let abaixo = 0;
    for (const filho of Array.from(el.children)) {
      // 4px de folga: card raspando a borda conta como visível, senão a pílula
      // apareceria dizendo "mais 1" com o card inteiro à vista.
      if ((filho as HTMLElement).getBoundingClientRect().bottom > limite + 4) abaixo++;
    }
    setOcultos(abaixo);
    setRolado(el.scrollTop > 4);
  }, []);

  useEffect(() => {
    medir();
    const el = ref.current;
    if (!el) return;
    // ResizeObserver cobre o redimensionamento da janela E a coluna esticando
    // porque a VIZINHA cresceu — o grid iguala as alturas, então a altura desta
    // muda sem que nada aqui dentro tenha mudado.
    const ro = new ResizeObserver(() => medir());
    ro.observe(el);
    return () => ro.disconnect();
  }, [medir, recalcularEm]);

  useEffect(() => () => { if (quadroRef.current) cancelAnimationFrame(quadroRef.current); }, []);

  const aoRolar = useCallback(() => {
    // rAF: o scroll dispara dezenas de vezes por gesto e cada medição lê o rect
    // de todos os cards, o que força layout. Uma medição por quadro basta.
    if (quadroRef.current) return;
    quadroRef.current = requestAnimationFrame(() => {
      quadroRef.current = 0;
      medir();
    });
  }, [medir]);

  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      <div
        ref={ref}
        onScroll={aoRolar}
        className={className}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {children}
      </div>

      {/* Sombras: pointer-events-none para não roubar o drop nem o clique do card. */}
      {rolado && (
        <div className="pointer-events-none absolute inset-x-0.5 top-0.5 h-5 bg-gradient-to-b from-central-fundo to-transparent" />
      )}
      {ocultos > 0 && (
        <div className="pointer-events-none absolute inset-x-0.5 bottom-0.5 h-10 bg-gradient-to-t from-central-fundo via-central-fundo/80 to-transparent rounded-b-lg" />
      )}
      {ocultos > 0 && !arrastando && (
        <button
          type="button"
          onClick={() => ref.current?.scrollBy({ top: ref.current.clientHeight * 0.8, behavior: 'smooth' })}
          title={`${ocultos} lead(s) abaixo — clique para rolar`}
          className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600 shadow hover:bg-slate-600 hover:text-white transition-colors"
        >
          ↓ mais {ocultos}
        </button>
      )}
    </div>
  );
}

const columnHelper = createColumnHelper<any>();

/**
 * O que o estado FAZ, dito sob o nome dele (regra da Central). Só onde o código prova a consequência (origin/main do
 * backend, 08/10/2026): Congelado sai do funil e das métricas e, com data, o cron das 8h (leads.scheduler) o devolve à
 * coluna de origem (statusBeforeFreeze); Perdido entra na conversão (ganhos / ganhos+perdidos, aqui no board) e
 * Reativar o leva a Novo, e o backend apaga lostAt e lostReason (leads.service, PRESERVA_FECHAMENTO). Os seis
 * estágios do funil NÃO têm subtítulo: nenhum dispara nada no sistema, e subtítulo inventado é o defeito que a
 * biblioteca combate.
 */
const CONSEQUENCIA = {
  congelado: 'Fora do funil e das métricas. Com data marcada, volta sozinho às 8h para a coluna de onde saiu; sem data, só pelo Descongelar.',
  perdido: 'Fora do funil, e conta na conversão. Reativar devolve o lead a Novo e apaga o motivo da perda.',
};

const LeadsBoard: React.FC<LeadsBoardProps> = ({
  leads,
  canEdit,
  loading,
  carga,
  onEdit,
  onStatusChange,
  onLoadData,
  onLeadUpdate,
  onLeadRemove,
  showToast,
  Icon,
  initialSearch,
  onSearchConsumed,
  propostas,
  funil = ETAPAS_FIXAS,
  onConcluirProximaAcao,
}) => {
  const [globalFilter, setGlobalFilter] = useState(initialSearch || '');
  useEffect(() => {
    if (initialSearch) { setGlobalFilter(initialSearch); onSearchConsumed?.(); }
  }, [initialSearch, onSearchConsumed]);
  const [sourceFilter, setSourceFilter] = useState('Todos');
  /** 'todos' | 'sem' | id do responsável (string). Ver bloco `responsaveis`. */
  // B2 (12/10/2026): três escopos no lugar do select por pessoa. Abre em Todos, como antes.
  const [assigneeFilter, setAssigneeFilter] = useState<EscopoDoResponsavel>('todos');
  const [activeTab, setActiveTabState] = useState<TabKey>(() => abaDaUrl(window.location.search));
  const setActiveTab = (t: TabKey) => {
    setActiveTabState(t);
    trocarParametroNaUrl('aba', t === 'pipeline' ? null : ABA_NA_URL[t]);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [density, setDensity] = useState<'compact' | 'detailed'>(() =>
    localStorage.getItem('crm_card_density') === 'detailed' ? 'detailed' : 'compact'
  );
  const [sortMode, setSortMode] = useState<'value' | 'stale' | 'recent'>(() => {
    // Default 'stale' (07/07, pedido do user): mais parado primeiro — a ordem
    // que induz ação. Quem escolher outra no select mantém (localStorage).
    const s = localStorage.getItem('crm_sort');
    return s === 'value' || s === 'recent' ? s : 'stale';
  });

  const changeDensity = useCallback((d: 'compact' | 'detailed') => {
    setDensity(d);
    localStorage.setItem('crm_card_density', d);
  }, []);

  const changeSortMode = useCallback((s: 'value' | 'stale' | 'recent') => {
    setSortMode(s);
    localStorage.setItem('crm_sort', s);
  }, []);

  // Modais
  const [freezeModal, setFreezeModal] = useState<any>(null);
  const [freezeForm, setFreezeForm] = useState({ frozenReason: '', frozenUntil: '' });
  const [lostModal, setLostModal] = useState<any>(null);
  const [lostReason, setLostReason] = useState('');
  // P2 (11/10/2026): excluir conta as propostas e o briefing e diz o que acontece com eles (era "Exclusão DEFINITIVA").
  const confirmar = useConfirm();
  // E2 (12/10/2026): o Congelado (a maior "coluna" real) entra no board como faixa recolhida, abaixo do funil.
  const [congeladosAbertos, setCongeladosAbertos] = useState(false);

  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);
  // B5 (12/10/2026): a anotação rápida, no topo do board. Só o nome ou só o CNPJ; Enter salva como Novo.
  const [anotacao, setAnotacao] = useState('');
  const [anotando, setAnotando] = useState(false);
  const anotarLead = async () => {
    const payload = payloadDaAnotacao(anotacao);
    if (!payload || anotando) return;
    setAnotando(true);
    try {
      await leadsService.create(payload);
      showToast(`${payload.companyName} anotado em Novo. Complete os dados na ficha.`, 'success');
      setAnotacao('');
      onLoadData();
    } catch (e: any) {
      showToast(`Não anotei ${payload.companyName}: ${fraseDaRecusaDaAnotacao(e)}`, 'error');
    }
    setAnotando(false);
  };
  const isAdmin = user?.role === 'admin';

  const globalFilterFn: FilterFn<any> = useCallback((row: any, _columnId: string, filterValue: any) => {
    const s = (filterValue as string).toLowerCase().trim();
    if (!s) return true;
    const l = row.original;
    const sDigits = s.replace(/\D/g, '');
    const leadCnpjDigits = (l.cnpj || '').replace(/\D/g, '');
    const matchText = (l.companyName || '').toLowerCase().includes(s) ||
      (l.tradeName || '').toLowerCase().includes(s) ||
      (l.contactName || '').toLowerCase().includes(s) ||
      (l.email || '').toLowerCase().includes(s);
    const matchCnpj = sDigits.length >= 3 && leadCnpjDigits.length > 0 && leadCnpjDigits.includes(sDigits);
    return matchText || matchCnpj;
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor('companyName', { id: 'companyName' }),
    columnHelper.accessor('tradeName', { id: 'tradeName' }),
  ], []);

  const table = useReactTable({
    data: leads,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  /**
   * Filtro por RESPONSÁVEL — a única dimensão que divide esta carteira.
   *
   * ⛔ O único filtro da barra era por ORIGEM, e origem não separa nada: 50 dos
   * 52 leads são "Outro". O select oferecia duas opções reais e a outra devolvia
   * 2 leads. Enquanto isso a divisão que existe de verdade — 24 leads de um
   * responsável, 11 de outro — não tinha filtro nenhum.
   *
   * É também o jeito mais direto de lidar com coluna cheia: "só os meus" corta
   * a coluna pela metade, coisa que nenhum indicador de scroll faz.
   *
   * NÃO fica salvo no localStorage, ao contrário da densidade e da ordenação.
   * Essas duas rearranjam; esta ESCONDE. Filtro que sobrevive ao reload manda a
   * pessoa procurar um lead que está lá e não aparece.
   */
  const responsaveis = useMemo(() => {
    const mapa = new Map<string, string>();
    let semDono = false;
    for (const l of leads) {
      const nome = l.assignedToUser?.name || l.assignedTo;
      if (l.assignedToId && nome) mapa.set(String(l.assignedToId), nome);
      else if (!nome) semDono = true;
    }
    return {
      lista: Array.from(mapa, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome)),
      temSemDono: semDono,
    };
  }, [leads]);

  const temMeus = !!user?.id && responsaveis.lista.some(r => r.id === String(user.id));

  const casaResponsavel = useCallback((l: any) => casaEscopo(l, assigneeFilter, user?.id), [assigneeFilter, user]);
  const contagemDoEscopo = useMemo(() => ({
    meus: leads.filter((l: any) => casaEscopo(l, 'meus', user?.id)).length,
    equipe: leads.filter((l: any) => casaEscopo(l, 'equipe', user?.id)).length,
    todos: leads.length,
  }), [leads, user]);
  // B6: as propostas por lead, para o visto/não visto do card.
  const propostasPorLead = useMemo(() => {
    const m = new Map<number, any[]>();
    for (const p of propostas || []) { if (p?.leadId) m.set(p.leadId, [...(m.get(p.leadId) || []), p]); }
    return m;
  }, [propostas]);
  const seloDoVisto = (lead: any) => {
    const v = vistoDoLead(propostasPorLead.get(lead.id) || []);
    if (!v) return null;
    return v.visto
      ? <span className='text-[10px] text-central-texto whitespace-nowrap' data-visto='sim' title={`O cliente abriu a proposta em ${fmtDateTime(v.em)}`}>👁 vista {fmtDate(v.em)}</span>
      : <span className='text-[10px] text-central-texto-fraco whitespace-nowrap' data-visto='nao' title='A proposta foi enviada e o cliente ainda não abriu o link'>○ não vista</span>;
  };
  const semResponsavel = (lead: any) => !(lead.assignedToId || lead.assignedToUser?.name || lead.assignedTo);

  // B1 (12/10/2026, #411): "Só atrasados (N)", a próxima ação com prazo vencido e não concluída. Conta sobre a lista que
  // o board já tem (as mesmas permissões); com o backend de hoje (sem a chave), o filtro nem aparece.
  const [soAtrasados, setSoAtrasados] = useState(false);
  const comProximaAcao = temProximaAcao(leads);
  const atrasados = leads.filter((l: any) => (atrasoDaProximaAcao(l) ?? 0) > 0);
  const filteredData = table.getFilteredRowModel().rows.map(r => r.original);
  const leadsFiltered = (sourceFilter === 'Todos' ? filteredData : filteredData.filter((l: any) => l.source === sourceFilter))
    .filter(casaResponsavel)
    .filter((l: any) => !soAtrasados || (atrasoDaProximaAcao(l) ?? 0) > 0);

  /**
   * Base dos KPIs do topo.
   *
   * Segue o filtro de responsável, mas NÃO a busca nem a origem. A distinção é
   * de propósito: escolher um responsável é trocar de ESCOPO ("o funil da
   * Camilla"), e aí somar o MRR da carteira inteira em cima de 11 cards dela
   * seria mentira. Digitar na busca é uma CONSULTA ("cadê a Autodefesa") — os
   * totais têm que ficar parados, senão cada tecla reescreve o painel.
   */
  const escopo = useMemo(() => leads.filter(casaResponsavel), [leads, casaResponsavel]);

  // Stats (congelado/arquivado NÃO contam no pipeline ativo)
  const pipelineLeads = escopo.filter((l: any) => !funil.terminais.includes(l.status));
  /**
   * Origem só vira badge quando DISCRIMINA alguma coisa.
   *
   * Hoje 57 dos 59 leads têm source "Outro". O badge aparecia em todo card, no
   * canto superior direito, disputando espaço com o nome da empresa — que é o
   * dado que importa e era justamente o que truncava ("CERTEZZA CONSULTORIA…").
   * Rótulo que nunca varia não informa, só ocupa.
   *
   * O corte é 80%: passando disso, a origem dominante fica implícita e só as
   * exceções aparecem. Se um dia a carteira diversificar, o badge volta sozinho
   * em todos — sem ninguém precisar mexer aqui.
   */
  const origemDominante = useMemo(() => {
    const leadsRelevantes = leads.filter((l: any) => l.source);
    if (leadsRelevantes.length < 5) return null; // amostra pequena: mostra tudo
    const contagem = new Map<string, number>();
    for (const l of leadsRelevantes) contagem.set(l.source, (contagem.get(l.source) || 0) + 1);
    let topo: string | null = null;
    let maior = 0;
    contagem.forEach((qtd, src) => {
      if (qtd > maior) { maior = qtd; topo = src; }
    });
    return maior / leadsRelevantes.length >= 0.8 ? topo : null;
  }, [leads]);

  const totalMRRPipeline = pipelineLeads.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValue) || 0), 0);
  const totalPSPipeline = pipelineLeads.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValuePS) || 0), 0);
  const closedLeads = escopo.filter((l: any) => l.status === 'Ganho');
  const lostLeads = escopo.filter((l: any) => l.status === 'Perdido');
  const frozenLeads = escopo.filter((l: any) => l.status === 'Congelado');
  const archivedLeads = escopo.filter((l: any) => l.status === 'Arquivado');
  const conversionRate = escopo.length > 0 ? ((closedLeads.length / (closedLeads.length + lostLeads.length)) * 100) || 0 : 0;
  const avgCycleTime = closedLeads.length > 0 ? closedLeads.reduce((sum: number, l: any) => {
    if (!l.wonAt || !l.createdAt) return sum;
    const days = (new Date(l.wonAt).getTime() - new Date(l.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    return sum + days;
  }, 0) / closedLeads.filter((l: any) => l.wonAt && l.createdAt).length : 0;
  const sources: string[] = Array.from(new Set(leads.map((l: any) => l.source).filter(Boolean)));

  /**
   * ⛔ Número sem fonte é "—", nunca 0 (08/10/2026). Os KPIs e as contagens das abas só têm fonte quando alguma carga
   * deu certo; antes disso (carga falhou de primeira, ou ainda não voltou) a tela dizia "R$ 0,00" e "0,0%", uma
   * afirmação sobre a carteira que ela não tinha lido. Numa RECARGA que falha, os números da última carga boa ficam,
   * com a faixa do EstadoDaLista dizendo de quando são.
   */
  const temFonte = carga.atualizadoEm !== null;
  const semFonte = textos.numeroSemFonte;
  const qtd = (n: number) => (temFonte ? String(n) : semFonte);
  const reais = (v: number) => (temFonte ? `R$ ${fmt(v)}` : semFonte);
  // Sem fonte não tem tom: o "—" de MRR e P&S sai em texto fraco, como os vizinhos. Verde ali diria "positivo" sobre um
  // número que não existe.
  const SEM_TOM = 'text-central-texto-fraco';

  // ---- Ações ----
  const openFreeze = useCallback((lead: any) => { setFreezeForm({ frozenReason: '', frozenUntil: '' }); setFreezeModal(lead); }, []);

  const confirmFreeze = useCallback(async () => {
    if (!freezeModal) return;
    try {
      const res = await leadsService.freeze(freezeModal.id, {
        frozenUntil: freezeForm.frozenUntil || undefined,
        reason: freezeForm.frozenReason || undefined,
      });
      onLeadUpdate({ id: freezeModal.id, ...res });
      showToast('❄ Lead congelado', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao congelar lead', 'error');
    }
    setFreezeModal(null);
    setFreezeForm({ frozenReason: '', frozenUntil: '' });
  }, [freezeModal, freezeForm, onLeadUpdate, showToast]);

  const handleUnfreeze = useCallback(async (lead: any) => {
    try {
      const res = await leadsService.unfreeze(lead.id);
      onLeadUpdate({ id: lead.id, ...res });
      showToast(`↩ Lead descongelado → ${res?.status || 'Contato'}`, 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao descongelar lead', 'error');
    }
  }, [onLeadUpdate, showToast]);

  const openLost = useCallback((lead: any) => { setLostReason(''); setLostModal(lead); }, []);

  const confirmLost = useCallback(() => {
    if (!lostModal || !lostReason.trim()) return;
    onStatusChange(lostModal.id, 'Perdido', lostReason.trim());
    setLostModal(null);
    setLostReason('');
  }, [lostModal, lostReason, onStatusChange]);

  const excluirLead = useCallback(async (lead: any) => {
    const nome = lead.tradeName || lead.companyName || `#${lead.id}`;
    const c: any = confirmacaoDeExcluirLead(nome, await lerImpactoDaExclusao(lead.id));
    if (c.recusa) { showToast(c.recusa, 'error'); return; }
    if (!(await confirmar(c))) return;
    try {
      await leadsService.delete(lead.id);
      onLeadRemove(lead.id);
      showToast('Lead excluído', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Erro ao excluir lead', 'error');
    }
  }, [confirmar, onLeadRemove, showToast]);

  // ---- Staleness (saúde do lead — só Kanban/Pipeline) ----
  /**
   * Dias desde a última atividade REAL do lead.
   *
   * ⛔ Media `updatedAt`, que errava dos dois lados:
   *
   *  - para MENOS: qualquer escrita na linha zerava o contador. Corrigir um
   *    telefone ou arrastar o card fazia um lead morto há meses voltar a
   *    parecer fresco — e, como a escala é por quartil, um registro tocado por
   *    engano ainda rebaixava o corte de toda a carteira.
   *  - para MAIS: proposta criada, contrato digital gerado e proposta aberta
   *    pelo cliente NÃO tocam a linha do lead. O lead onde a proposta foi
   *    enviada ontem aparecia parado há 60 dias.
   *
   * `lastActivityAt` vem do backend agregando `activity_logs` (o mesmo que
   * alimenta a aba Histórico do lead). Cai para `updatedAt` quando é null —
   * lead anterior ao logging.
   */
  const staleDaysOf = (lead: any): number => {
    const base = lead.lastActivityAt || lead.updatedAt;
    if (!base) return 0;
    const d = Math.floor((Date.now() - new Date(base).getTime()) / 86400000);
    return isNaN(d) || d < 0 ? 0 : d;
  };
  /**
   * Corte de "parado" pela DISTRIBUIÇÃO da carteira, não por número fixo.
   *
   * ⛔ Antes era ≥30d vermelho, ≥14d âmbar. Parece razoável no abstrato, mas
   * nesta carteira quase tudo passa de 30 dias (170, 128, 116, 103, 80, 68,
   * 67, 66, 62, 55, 52, 44, 41, 40…) — ou seja, PRATICAMENTE TODO CARD FICAVA
   * VERMELHO. Vermelho em todo lugar não alerta ninguém, e 170d renderizava
   * igual a 40d, que é justamente a diferença que importa ver de relance.
   *
   * Agora só o pior quartil grita e a mediana avisa, então o destaque
   * acompanha a carteira: se um dia tudo estiver fresco, o corte desce junto.
   *
   * O PISO ABSOLUTO continua: por pior que seja o quartil, nada abaixo de 14
   * dias vira alerta — senão, numa carteira toda nova, um lead de 3 dias
   * apareceria como crítico só por ser o mais velho de um grupo jovem.
   */
  const PISO_ALERTA_DIAS = 14;
  const cortesStale = useMemo(() => {
    const dias = pipelineLeads
      .map((l: any) => staleDaysOf(l))
      .filter((d: number) => d > 0)
      .sort((a: number, b: number) => a - b);
    // Amostra pequena: percentil não significa nada. Volta pro corte absoluto.
    if (dias.length < 8) return { grave: 30, atencao: PISO_ALERTA_DIAS };
    const p = (q: number) => dias[Math.min(dias.length - 1, Math.floor(dias.length * q))];
    const atencao = Math.max(PISO_ALERTA_DIAS, p(0.5));
    // Se o p75 empatar com a mediana (ou cair sob o piso), os dois cortes viram
    // um só e TUDO que alerta alerta em vermelho — exatamente o que se quis
    // evitar. Um dia de folga preserva os dois níveis.
    return { grave: Math.max(atencao + 1, p(0.75)), atencao };
  }, [pipelineLeads]); // eslint-disable-line react-hooks/exhaustive-deps

  const staleBorderClass = (days: number) =>
    days >= cortesStale.grave
      ? 'border-l-red-500/80'
      : days >= cortesStale.atencao
        ? 'border-l-amber-500/70'
        : 'border-l-emerald-500/60';

  const renderStaleBadge = (days: number) =>
    days >= cortesStale.atencao ? (
      <span
        // Estado por peso + ícone (a barra é a borda esquerda do card, staleBorderClass), sem cor no texto: o pior
        // quartil ganha ⚠ e negrito, a mediana fica no 🕐 em peso normal.
        className={`text-[9px] px-1 py-0.5 rounded flex-shrink-0 whitespace-nowrap bg-central-elevado text-central-texto ${
          days >= cortesStale.grave ? 'font-bold' : 'font-normal'
        }`}
        title={
          `sem movimentação há ${days} dias`
          + ` — nesta carteira, "parado" começa em ${cortesStale.atencao}d e o pior quartil em ${cortesStale.grave}d`
        }
      >
        {days >= cortesStale.grave ? '⚠' : '🕐'} {days}d
      </span>
    ) : null;

  // ---- Ordenação dentro das colunas do Kanban ----
  const sortKanbanLeads = (arr: any[]): any[] => {
    const sorted = [...arr];
    // Mesma base do badge (lastActivityAt, com updatedAt de reserva). Ordenar
    // por um critério e rotular por outro faria a coluna listar "mais parado"
    // numa ordem que não bate com os dias escritos nos próprios cards.
    const quando = (l: any) => {
      const base = l.lastActivityAt || l.updatedAt;
      return base ? new Date(base).getTime() : 0;
    };
    if (sortMode === 'stale') {
      sorted.sort((a, b) => quando(a) - quando(b));
    } else if (sortMode === 'recent') {
      sorted.sort((a, b) => quando(b) - quando(a));
    } else {
      sorted.sort((a, b) => {
        const va = parseFloat(a.estimatedValue); const vb = parseFloat(b.estimatedValue);
        const na = isNaN(va) ? -Infinity : va; const nb = isNaN(vb) ? -Infinity : vb;
        return nb - na; // desc, null/NaN por último
      });
    }
    return sorted;
  };

  const cardTooltip = (lead: any) =>
    [
      lead.cnpj ? `CNPJ: ${fmtCNPJ(lead.cnpj)}` : null,
      lead.contactName ? `Contato: ${lead.contactName}` : null,
      lead.phone ? `Tel: ${lead.phone}` : null,
      lead.email ? `Email: ${lead.email}` : null,
    ].filter(Boolean).join('\n');

  /**
   * Cor da coluna como ACENTO, não como bloco.
   *
   * Eram seis faixas saturadas no topo (gradientes cheios em azul, âmbar,
   * ciano, roxo, laranja, verde) — o elemento mais forte da tela carregando a
   * informação menos útil: o nome do estágio já está escrito e a posição da
   * coluna já o identifica. Isso roubava atenção dos dados, que é o que se lê.
   *
   * Depois (12/10/2026, PR-2 de cor do CRM, molde da Central do Fábio): a etapa é POSIÇÃO, não matiz. Seis matizes
   * para seis etapas não dizem nada que o nome e a ordem já não digam, e a Negociação levava o laranja do acento.
   * A coluna tem a barra superior neutra e o número da etapa; o card, a borda neutra. A cor fica para o que é
   * significado (o lead parado, na borda esquerda do card).
   */
  // A4: as colunas são as etapas ATIVAS do servidor (#409), na ordem; sem a #409, as seis fixas.
  const ETAPAS_DO_FUNIL = funil.ativas;
  const columnColors: any = Object.fromEntries(ETAPAS_DO_FUNIL.map((etapa) => [etapa, {
    accent: 'border-t-slate-500', card: 'border-central-borda',
  }]));

  /**
   * B1: a próxima ação no card, "→ ligar para a Karem · 15/10/2026". Atrasada leva o molde das vencidas de Recebíveis:
   * ícone no tom do atraso (âmbar até 7 dias, vermelho depois) e o atraso em negrito. A barra lateral do card já é do
   * "parado", então aqui não há barra.
   */
  const linhaDaProximaAcao = (lead: any) => {
    if (!lead?.nextActionText || lead?.nextActionDoneAt) return null;
    const atraso = atrasoDaProximaAcao(lead) ?? 0;
    const tom = tomDoAtraso(atraso);
    return (
      <div className='flex items-center gap-1 text-[10px] mt-1 min-w-0' data-proxima-acao-do-card={tom || 'em-dia'}>
        {tom && <IconeDoHub name='alerta' className={`w-3 h-3 shrink-0 ${TONE_TEXT[tom]}`} />}
        <span className={`truncate ${tom ? 'font-semibold text-white' : 'text-slate-400'}`} title={lead.nextActionText}>
          → {lead.nextActionText}{lead.nextActionAt ? ` · ${fmtDate(lead.nextActionAt)}` : ''}{tom ? ` · atrasada há ${atraso} ${atraso === 1 ? 'dia' : 'dias'}` : ''}
        </span>
        {canEdit && onConcluirProximaAcao && (
          <button type='button' onClick={(e) => { e.stopPropagation(); onConcluirProximaAcao(lead); }} title='Concluir a próxima ação' className='shrink-0 px-1 rounded text-slate-400 hover:text-white hover:bg-central-elevado'>✓</button>
        )}
      </div>
    );
  };

  const renderResponsavel = (lead: any) => (
    (lead.assignedToUser?.name || lead.assignedTo) ? (
      <div className='flex items-center gap-1.5'>
        <span className={`w-5 h-5 rounded-full ${getAvatarColor(lead.assignedToId)} text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0`}>
          {(lead.assignedToUser?.name || lead.assignedTo || '').split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
        </span>
        <span className='text-slate-400'>{lead.assignedToUser?.name || lead.assignedTo}</span>
      </div>
    ) : <span className='text-slate-600'>—</span>
  );

  // Tabela Ganhos / Perdidos
  const renderWonLostTable = (status: 'Ganho' | 'Perdido', leadsForTable: any[]) => (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-central-borda'>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Empresa</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Contato</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Responsável</th>
            <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Fechamento</th>
            {status === 'Perdido' && <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Motivo</th>}
            <th className='text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase'>P&S</th>
            <th className='text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase'>MRR</th>
            {canEdit && <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Ação</th>}
          </tr>
        </thead>
        <tbody>
          {leadsForTable.map((lead: any) => (
            <tr key={lead.id} className='border-b border-central-borda hover:bg-central-elevado cursor-pointer' onClick={() => onEdit(lead)}>
              <td className='py-2 px-3'>
                <p className='font-medium text-white'>{lead.tradeName || lead.companyName}</p>
                {lead.tradeName && <p className='text-xs text-slate-500'>{lead.companyName}</p>}
              </td>
              <td className='py-2 px-3 text-slate-400'>{lead.contactName}</td>
              <td className='py-2 px-3 text-xs'>{renderResponsavel(lead)}</td>
              <td className='py-2 px-3 text-center text-slate-400 text-xs'>{status === 'Ganho' ? fmtDate(lead.wonAt) : fmtDate(lead.lostAt)}</td>
              {status === 'Perdido' && <td className='py-2 px-3 text-slate-500 text-xs max-w-[150px] truncate' title={lead.lostReason || ''}>{lead.lostReason || '—'}</td>}
              <td className={`py-2 px-3 text-right ${TONE_TEXT.ps}`}>R$ {fmt(lead.estimatedValuePS || 0)}</td>
              <td className={`py-2 px-3 text-right ${TONE_TEXT.open} font-bold`}>R$ {fmt(lead.estimatedValue || 0)}</td>
              {canEdit && (
                <td className='py-2 px-3 text-center' onClick={e => e.stopPropagation()}>
                  {status === 'Ganho' && (
                    <div className='flex items-center justify-center gap-2'>
                      <span className='text-xs text-slate-400'>{fmtDate(lead.wonAt)}</span>
                      <button onClick={() => onStatusChange(lead.id, 'Arquivado')} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_FANTASMA}`}>Arquivar</button>
                    </div>
                  )}
                  {status === 'Perdido' && (
                    <div className='flex items-center justify-center gap-2'>
                      <button onClick={() => onStatusChange(lead.id, 'Novo')} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_FANTASMA}`}>Reativar</button>
                      {isAdmin && <button onClick={() => excluirLead(lead)} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_PERIGO}`}>Excluir</button>}
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Tabela Congelados
  const renderFrozenTable = (leadsForTable: any[]) => (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-central-borda'>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Empresa</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Contato</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Responsável</th>
            <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Congelado em</th>
            <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Até</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Motivo</th>
            <th className='text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase'>MRR</th>
            {canEdit && <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Ação</th>}
          </tr>
        </thead>
        <tbody>
          {leadsForTable.map((lead: any) => (
            <tr key={lead.id} className='border-b border-central-borda hover:bg-central-elevado cursor-pointer' onClick={() => onEdit(lead)}>
              <td className='py-2 px-3'>
                <p className='font-medium text-white'>{lead.tradeName || lead.companyName}</p>
                {lead.tradeName && <p className='text-xs text-slate-500'>{lead.companyName}</p>}
              </td>
              <td className='py-2 px-3 text-slate-400'>{lead.contactName}</td>
              <td className='py-2 px-3 text-xs'>{renderResponsavel(lead)}</td>
              <td className='py-2 px-3 text-center text-slate-400 text-xs'>{fmtDate(lead.updatedAt)}</td>
              <td className='py-2 px-3 text-center text-cyan-400 text-xs'>{lead.frozenUntil ? fmtDate(lead.frozenUntil) : '—'}</td>
              <td className='py-2 px-3 text-slate-500 text-xs max-w-[180px] truncate' title={lead.frozenReason || ''}>{lead.frozenReason || '—'}</td>
              <td className={`py-2 px-3 text-right ${TONE_TEXT.open} font-bold`}>R$ {fmt(lead.estimatedValue || 0)}</td>
              {canEdit && (
                <td className='py-2 px-3 text-center' onClick={e => e.stopPropagation()}>
                  <div className='flex items-center justify-center gap-2'>
                    <button onClick={() => handleUnfreeze(lead)} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_FANTASMA}`}>Descongelar</button>
                    {isAdmin && <button onClick={() => excluirLead(lead)} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_PERIGO}`}>Excluir</button>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Tabela Arquivados
  const renderArchivedTable = (leadsForTable: any[]) => (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-central-borda'>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Empresa</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Contato</th>
            <th className='text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Responsável</th>
            <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Arquivado em</th>
            <th className='text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase'>MRR</th>
            {canEdit && <th className='text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase'>Ação</th>}
          </tr>
        </thead>
        <tbody>
          {leadsForTable.map((lead: any) => (
            <tr key={lead.id} className='border-b border-central-borda hover:bg-central-elevado cursor-pointer' onClick={() => onEdit(lead)}>
              <td className='py-2 px-3'>
                <p className='font-medium text-white'>{lead.tradeName || lead.companyName}</p>
                {lead.tradeName && <p className='text-xs text-slate-500'>{lead.companyName}</p>}
              </td>
              <td className='py-2 px-3 text-slate-400'>{lead.contactName}</td>
              <td className='py-2 px-3 text-xs'>{renderResponsavel(lead)}</td>
              <td className='py-2 px-3 text-center text-slate-400 text-xs'>{fmtDate(lead.updatedAt)}</td>
              <td className={`py-2 px-3 text-right ${TONE_TEXT.open} font-bold`}>R$ {fmt(lead.estimatedValue || 0)}</td>
              {canEdit && (
                <td className='py-2 px-3 text-center' onClick={e => e.stopPropagation()}>
                  <div className='flex items-center justify-center gap-2'>
                    <button onClick={() => onStatusChange(lead.id, 'Contato')} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_FANTASMA}`}>Restaurar</button>
                    {isAdmin && <button onClick={() => excluirLead(lead)} className={`text-xs px-2 py-1 rounded font-medium ${BOTAO_PERIGO}`}>Excluir</button>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const wonLeads = leadsFiltered.filter((l: any) => l.status === 'Ganho').sort((a: any, b: any) => (b.wonAt ? new Date(b.wonAt).getTime() : 0) - (a.wonAt ? new Date(a.wonAt).getTime() : 0));
  const lostLeadsFiltered = leadsFiltered.filter((l: any) => l.status === 'Perdido').sort((a: any, b: any) => (b.lostAt ? new Date(b.lostAt).getTime() : 0) - (a.lostAt ? new Date(a.lostAt).getTime() : 0));
  const frozenLeadsFiltered = leadsFiltered.filter((l: any) => l.status === 'Congelado');
  const archivedLeadsFiltered = leadsFiltered.filter((l: any) => l.status === 'Arquivado');

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'won', label: `✅ Ganhos (${qtd(closedLeads.length)})` },
    { key: 'lost', label: `❌ Perdidos (${qtd(lostLeads.length)})` },
    { key: 'frozen', label: `❄️ Congelados (${qtd(frozenLeads.length)})` },
    { key: 'archived', label: `📦 Arquivados (${qtd(archivedLeads.length)})` },
  ];

  const emptyBlock = (icon: string, text: string) => (
    <div className='py-10 text-center text-slate-600'>
      <Icon name={icon} className='w-8 h-8 mx-auto mb-2 opacity-50' />
      <p className='text-sm'>{text}</p>
    </div>
  );

  return (
    <div className='space-y-4'>
      {/* Faixa de indicadores.
          Eram SEIS cards de 140px de altura, todos com o mesmo peso visual,
          antes do primeiro lead — em 1080p o board só começava lá pelos 520px.
          Compactado numa faixa única sobram ~90px, que é mais um lead visível
          por coluna. O peso agora segue o uso: MRR e P&S grandes (é o que se
          olha o tempo todo), o resto em corpo menor. */}
      <div className={`${SUPERFICIE} px-4 py-3`}>
        <div className='flex flex-wrap items-center gap-x-8 gap-y-3'>
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>MRR Potencial</p>
            <p className={`text-xl font-bold ${temFonte ? TONE_TEXT.open : SEM_TOM} leading-tight mt-1`} data-tom-kpi='mrr'>{reais(totalMRRPipeline)}</p>
          </div>
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>P&S Potencial</p>
            <p className={`text-xl font-bold ${temFonte ? TONE_TEXT.ps : SEM_TOM} leading-tight mt-1`} data-tom-kpi='ps'>{reais(totalPSPipeline)}</p>
          </div>
          <div className='h-8 w-px bg-central-borda hidden md:block' />
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>Pipeline</p>
            <p className='text-sm font-semibold text-white leading-tight mt-1'>
              {temFonte ? `${pipelineLeads.length} ativo(s)` : semFonte}
              {frozenLeads.length > 0 && <span className='text-central-texto-fraco font-normal'> · ❄ {frozenLeads.length}</span>}
            </p>
          </div>
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>Ganhos</p>
            <p className='text-sm font-semibold text-central-texto leading-tight mt-1'>
              {qtd(closedLeads.length)}
              {temFonte && <span className='text-slate-500 font-normal'> · {reais(closedLeads.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValue) || 0), 0))}</span>}
            </p>
          </div>
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>Conversão</p>
            <p className='text-sm font-semibold text-central-texto leading-tight mt-1'>
              {temFonte ? `${conversionRate.toFixed(1)}%` : semFonte}
              {temFonte && <span className='text-slate-500 font-normal'> · {closedLeads.length}/{closedLeads.length + lostLeads.length}</span>}
            </p>
          </div>
          <div className='min-w-0'>
            <p className='text-[11px] text-slate-400 leading-none'>Ciclo médio</p>
            <p className='text-sm font-semibold text-central-texto leading-tight mt-1'>
              {avgCycleTime > 0 ? `${Math.round(avgCycleTime)}d` : '—'}
            </p>
          </div>
        </div>
      </div>

      <ListToolbar
        search={<ToolbarSearch value={globalFilter} onChange={setGlobalFilter} placeholder='Buscar por empresa, contato, CNPJ ou email...' />}
        filters={
          <div className='flex items-center gap-2 flex-wrap'>
            {/* B2 (12/10/2026): Meus · Da equipe · Todos, com a contagem, no lugar do select por pessoa. */}
            <div role='group' aria-label='Responsável' className='inline-flex rounded-lg border border-central-borda overflow-hidden'>
              {([['meus', 'Meus'], ['equipe', 'Da equipe'], ['todos', 'Todos']] as const).map(([chave, rotulo]) => (
                <button
                  key={chave}
                  type='button'
                  aria-pressed={assigneeFilter === chave}
                  onClick={() => setAssigneeFilter(chave)}
                  className={`px-3 py-2 text-sm border-l border-central-borda first:border-l-0 transition-colors ${assigneeFilter === chave ? 'bg-central-elevado text-central-texto font-semibold' : 'bg-central-cartao text-central-texto-fraco hover:bg-central-elevado'}`}
                >
                  {rotulo} <span className='tabular-nums'>{contagemDoEscopo[chave]}</span>
                </button>
              ))}
            </div>
            {comProximaAcao && (
              <button
                type='button'
                aria-pressed={soAtrasados}
                onClick={() => setSoAtrasados((v) => !v)}
                className={`px-3 py-2.5 rounded-lg text-sm border border-central-borda transition-colors ${soAtrasados ? 'bg-central-elevado text-central-texto font-semibold' : 'bg-central-cartao text-central-texto-fraco hover:bg-central-elevado'}`}
              >
                Só atrasados ({atrasados.length})
              </button>
            )}
            {/* Origem só entra na barra quando há mais de uma para escolher.
                Com uma só, o select é um botão que não faz nada. */}
            {sources.length > 1 && (
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className={`px-4 py-2.5 ${campo} rounded-lg text-sm`}>
                <option value='Todos'>Todas origens</option>
                {sources.map(src => <option key={src} value={src}>{src}</option>)}
              </select>
            )}
          </div>
        }
        // "Novo Lead" só com a carga PRONTA: com os leads sem chegar, criar um é criar sem ver se ele já existe.
        actions={canEdit && carga.estado === 'pronto' && <button onClick={() => onEdit(null as any)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium whitespace-nowrap ${BOTAO_FANTASMA}`}><Icon name='plus' className='w-5 h-5' />Novo Lead</button>}
        secondRow={<div className='w-full flex items-center justify-between gap-2 flex-wrap'>
          <div className='flex gap-2 flex-wrap'>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {activeTab === 'pipeline' && (
            <div className='flex items-center gap-2 flex-shrink-0'>
              <select
                value={sortMode}
                onChange={e => changeSortMode(e.target.value as 'value' | 'stale' | 'recent')}
                title='Ordenação dos cards dentro das colunas'
                className={`px-2 py-1.5 rounded-lg text-xs ${campo}`}
              >
                {/* "Valor" prometia mais do que entrega: o sort olha só
                    estimatedValue (MRR) e ignora o P&S, que o card e o
                    cabeçalho da coluna mostram lado a lado. O rótulo agora diz
                    qual dos dois manda. */}
                <option value='value'>Ordenar: MRR ↓</option>
                <option value='stale'>Ordenar: Mais parado</option>
                <option value='recent'>Ordenar: Recente</option>
              </select>
              <div className='flex rounded-lg border border-slate-700 overflow-hidden'>
                <button
                  onClick={() => changeDensity('compact')}
                  title='Cards compactos'
                  className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${density === 'compact' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'}`}
                >
                  ▤ Compacto
                </button>
                <button
                  onClick={() => changeDensity('detailed')}
                  title='Cards detalhados'
                  className={`px-2.5 py-1.5 text-xs font-medium transition-colors border-l border-slate-700 ${density === 'detailed' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'}`}
                >
                  ☰ Detalhado
                </button>
              </div>
            </div>
          )}
        </div>}
      />

      {/* A4: a etapa da assinatura torta (o aceite da proposta não move lead) e a leitura das etapas que falhou. */}
      {funil.aviso && (
        <p role='alert' className={`px-3 py-2 rounded border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-aviso-do-funil>
          {funil.aviso}
        </p>
      )}
      {funil.erro && (
        <p role='status' className='text-xs text-central-texto-fraco' data-funil-nao-lido>
          Não li as etapas do funil ({funil.erro}); mostrando as seis padrão.
        </p>
      )}

      {/* B5: a linha de anotação rápida (o "Anotar uma tarefa… Enter salva" da Central). Mesma condição do "Novo Lead". */}
      {canEdit && carga.estado === 'pronto' && (
        <input
          type='text'
          value={anotacao}
          onChange={(e) => setAnotacao(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); anotarLead(); } }}
          disabled={anotando}
          placeholder='Anotar um lead: nome ou CNPJ, Enter salva em Novo'
          aria-label='Anotar um lead'
          className={`w-full px-4 py-2.5 rounded-lg text-sm ${campo}`}
        />
      )}

      {loading ? (
        <div className={`${SUPERFICIE} p-10 text-center text-central-texto-fraco`}>
          Carregando leads…
        </div>
      ) : (
        // Carregando, erro ou a lista: a carga é tudo-ou-nada (um Promise.all), então não há parcial aqui. Com dado da
        // última carga boa e a recarga falhando, o funil FICA e ganha a faixa. O vazio de verdade, cada aba e cada
        // coluna já dizem ("Arraste aqui", "Nenhum lead ganho ainda").
        <EstadoDaLista
          carga={carga}
          temDados={temFonte}
          oQue='os leads'
          onTentarDeNovo={onLoadData}
          vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}
        >
          {/* Tab: Pipeline (Kanban) */}
          {activeTab === 'pipeline' && (
            <div className='grid grid-cols-6 gap-3'>
              {ETAPAS_DO_FUNIL.map(status => {
                const statusLeads = sortKanbanLeads(leadsFiltered.filter((l: any) => l.status === status));
                const totalMRR = statusLeads.reduce((sum: number, l: any) => sum + (parseFloat(l.estimatedValue) || 0), 0);
                const totalPS = statusLeads.reduce((sum: number, l: any) => sum + (parseFloat(l.estimatedValuePS) || 0), 0);
                const colors = columnColors[status];
                return (
                  <div key={status} className='flex flex-col'>
                    <div className={`bg-central-cartao border-t-2 ${colors.accent} border-x border-central-borda px-3 py-2.5 rounded-t-lg`}>
                      <div className='flex items-center justify-between mb-1'>
                        <span className='font-semibold text-sm text-slate-200 flex items-center gap-2 min-w-0'>
                          <span className='text-[10px] font-semibold text-slate-400 tabular-nums shrink-0' title={`Etapa ${ETAPAS_DO_FUNIL.indexOf(status) + 1} de ${ETAPAS_DO_FUNIL.length}`}>{ETAPAS_DO_FUNIL.indexOf(status) + 1}</span>
                          <span className='min-w-0'>
                            <span className='truncate block'>{status}</span>
                            {/* B4: o que a etapa faz (ou significa), sem promessa. */}
                            {O_QUE_A_ETAPA_FAZ[status] && <span className='block truncate text-[10px] font-normal text-slate-400' data-o-que-a-etapa-faz>{O_QUE_A_ETAPA_FAZ[status]}</span>}
                          </span>
                        </span>
                        <span className='bg-central-elevado text-central-texto px-2 py-0.5 rounded-full text-xs font-semibold shrink-0'>{statusLeads.length}</span>
                      </div>
                      {/* Totais zerados somem: é o mesmo ruído que saiu dos cards.
                          "MRR: R$ 0,00 · P&S: R$ 0,00" em coluna vazia só ocupa. */}
                      {(totalMRR > 0 || totalPS > 0) ? (
                        <div className='flex justify-between text-[10px] text-slate-400'>
                          {totalMRR > 0 && <span>MRR: R$ {fmt(totalMRR)}</span>}
                          {totalPS > 0 && <span>P&S: R$ {fmt(totalPS)}</span>}
                        </div>
                      ) : (
                        <div className='text-[10px] text-slate-600'>sem valor</div>
                      )}
                    </div>

                    <ColunaRolavel
                      className='bg-central-fundo border border-t-0 border-central-borda rounded-b-lg p-2 space-y-2 flex-1 min-h-[400px] max-h-[calc(100vh-380px)] overflow-y-auto'
                      // Remede quando a lista muda de tamanho OU quando o card
                      // muda de altura (compacto ↔ detalhado).
                      recalcularEm={`${statusLeads.length}:${density}`}
                      arrastando={isDragging}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-primary'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('ring-2', 'ring-primary');
                        const leadId = e.dataTransfer.getData('leadId');
                        if (!leadId) return;
                        const lead = leads.find((l: any) => l.id === parseInt(leadId));
                        if (lead && lead.status !== status) onStatusChange(parseInt(leadId), status);
                      }}
                    >
                      {statusLeads.map((lead: any) => {
                        const staleDays = staleDaysOf(lead);
                        return (
                        <div
                          key={lead.id}
                          draggable={canEdit}
                          onDragStart={(e) => { e.dataTransfer.setData('leadId', lead.id.toString()); e.currentTarget.classList.add('opacity-50'); setIsDragging(true); }}
                          onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); setIsDragging(false); }}
                          className={`bg-central-elevado rounded-lg border ${colors.card} border-l-2 ${staleBorderClass(staleDays)} hover:border-slate-500 transition-all ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} group relative`}
                          onClick={() => onEdit(lead)}
                          title={density === 'compact' ? cardTooltip(lead) : undefined}
                        >
                          {canEdit && (
                            <div className='absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10'>
                              <button
                                onClick={(e) => { e.stopPropagation(); openFreeze(lead); }}
                                className={`p-1 rounded ${BOTAO_FANTASMA}`}
                                title='Congelar lead'
                              >
                                <span className='text-[11px] leading-none'>❄</span>
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); excluirLead(lead); }}
                                  className={`p-1 rounded bg-central-cartao border border-central-destrutivo text-central-destrutivo hover:bg-central-elevado`}
                                  title='Excluir lead'
                                >
                                  <Icon name='trash' className='w-3 h-3' />
                                </button>
                              )}
                            </div>
                          )}
                          {density === 'compact' ? (
                            <div className='p-2'>
                              <div className='flex items-start justify-between gap-1'>
                                <h4 className='font-medium text-white text-xs leading-tight truncate' title={lead.tradeName || lead.companyName}>{lead.tradeName || lead.companyName}</h4>
                                {lead.source && lead.source !== origemDominante && (
                                  <span className='text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded flex-shrink-0'>{lead.source}</span>
                                )}
                              </div>
                              {/* Produção (12/10/2026, visto pelo Heitor): o "vista DD/MM/AAAA" ficava nesta linha e, sem corte, passava
                                  POR CIMA do "MRR R$ …" (EUCARD, CERTEZZA, ALLMAYER). O lado esquerdo agora corta (overflow-hidden)
                                  e o visto mora na linha de baixo, junto da próxima ação: o MRR nunca é coberto. */}
                              <div className='flex items-center justify-between gap-1.5 mt-1 text-[10px]' data-rodape-compacto>
                                <div className='flex items-center gap-1.5 min-w-0 overflow-hidden'>
                                  {(lead.assignedToUser?.name || lead.assignedTo) && (
                                    <span className={`w-4 h-4 rounded-full ${getAvatarColor(lead.assignedToId)} text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0`} title={lead.assignedToUser?.name || lead.assignedTo}>
                                      {(lead.assignedToUser?.name || lead.assignedTo || '').split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                    </span>
                                  )}
                                  {semResponsavel(lead) && <span className='text-[10px] text-central-texto-fraco whitespace-nowrap' data-sem-responsavel>sem responsável</span>}
                                  {renderStaleBadge(staleDays)}
                                </div>
                                {/* Valor zero não se imprime. A coluna "Novo"
                                    tem 7 leads e os 7 sem MRR: era "R$ 0,00" em
                                    verde e negrito, sete vezes, ensinando o olho
                                    a ignorar justamente o campo que nas outras
                                    colunas é o mais importante da tela. */}
                                {parseFloat(lead.estimatedValue) > 0 && (
                                  <span className={`${TONE_TEXT.open} font-bold flex-shrink-0`}>MRR R$ {fmt(lead.estimatedValue)}</span>
                                )}
                              </div>
                              {seloDoVisto(lead) && <div className='mt-1 leading-none' data-visto-compacto>{seloDoVisto(lead)}</div>}
                              {linhaDaProximaAcao(lead)}
                            </div>
                          ) : (
                          <div className='p-2.5'>
                            <div className='flex items-start justify-between gap-1 mb-1'>
                              <h4 className='font-bold text-white text-xs leading-tight line-clamp-2'>{lead.tradeName || lead.companyName}</h4>
                              <div className='flex items-center gap-1 flex-shrink-0'>
                                {renderStaleBadge(staleDays)}
                                {lead.source && lead.source !== origemDominante && (
                                  <span className='text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded'>{lead.source}</span>
                                )}
                              </div>
                            </div>
                            {lead.cnpj && (
                              <div className='text-[10px] text-slate-500 font-mono mb-1.5'>{fmtCNPJ(lead.cnpj)}</div>
                            )}
                            <div className='flex items-center gap-1.5 text-[11px] text-slate-400 mb-1'>
                              <Icon name='user' className='w-3 h-3' />
                              <span className='truncate'>{lead.contactName || '—'}</span>
                            </div>
                            {(semResponsavel(lead) || vistoDoLead(propostasPorLead.get(lead.id) || [])) && (
                              <div className='flex items-center gap-2 mb-1'>
                                {semResponsavel(lead) && <span className='text-[10px] text-central-texto-fraco whitespace-nowrap' data-sem-responsavel>sem responsável</span>}
                                {seloDoVisto(lead)}
                              </div>
                            )}
                            {linhaDaProximaAcao(lead)}
                            {lead.phone && (
                              <div className='flex items-center gap-1.5 text-[10px] text-slate-500 mb-1'>
                                <Icon name='phone' className='w-3 h-3' />
                                <span className='truncate'>{lead.phone}</span>
                              </div>
                            )}
                            {lead.email && (
                              <div className='flex items-center gap-1.5 text-[10px] text-slate-500 mb-1.5'>
                                <Icon name='mail' className='w-3 h-3' />
                                <span className='truncate'>{lead.email}</span>
                              </div>
                            )}
                            <div className='flex items-center justify-between pt-1.5 border-t border-slate-700 text-[10px]'>
                              <div className='flex items-center gap-1.5'>
                                {(lead.assignedToUser?.name || lead.assignedTo) && (
                                  <span className={`w-4 h-4 rounded-full ${getAvatarColor(lead.assignedToId)} text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0`} title={lead.assignedToUser?.name || lead.assignedTo}>
                                    {(lead.assignedToUser?.name || lead.assignedTo || '').split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                  </span>
                                )}
                                {parseFloat(lead.estimatedValuePS) > 0 && (
                                  <span className={`${TONE_TEXT.ps} font-medium`}>P&S R$ {fmt(lead.estimatedValuePS)}</span>
                                )}
                              </div>
                              {parseFloat(lead.estimatedValue) > 0 && (
                                <span className={`${TONE_TEXT.open} font-bold`}>MRR R$ {fmt(lead.estimatedValue)}</span>
                              )}
                            </div>
                          </div>
                          )}
                        </div>
                      );})}

                      {statusLeads.length === 0 && (
                        <div className='flex flex-col items-center justify-center py-6 text-slate-600'>
                          <Icon name='clipboard' className='w-8 h-8 mb-1' />
                          <p className='text-xs'>Arraste aqui</p>
                        </div>
                      )}
                    </ColunaRolavel>
                  </div>
                );
              })}
            </div>
          )}
          {activeTab === 'pipeline' && frozenLeadsFiltered.length > 0 && (
            <div className='mt-3 border border-central-borda rounded-lg bg-central-cartao' data-testid='faixa-congelados'>
              <button
                type='button'
                onClick={() => setCongeladosAbertos((v) => !v)}
                aria-expanded={congeladosAbertos}
                className='w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white'
              >
                <span>❄ Congelado ({frozenLeadsFiltered.length}) <span className='text-slate-500'>· fora do funil e das métricas</span></span>
                <span className='text-xs text-slate-400'>{congeladosAbertos ? 'Recolher' : 'Mostrar'}</span>
              </button>
              {congeladosAbertos && <div className='px-2 pb-2'>{renderFrozenTable(frozenLeadsFiltered)}</div>}
            </div>
          )}

          {/* Tab: Ganhos */}
          {activeTab === 'won' && (
            <div className={`${SUPERFICIE} p-4`}>
              <h3 className='font-semibold text-white flex items-center gap-2 mb-3'>
                <span className='w-3 h-3 rounded-full bg-green-500'></span>
                Leads Ganhos ({wonLeads.length})
                <span className={`text-xs ${TONE_TEXT.open} font-normal ml-2`}>MRR ganho: R$ {fmt(wonLeads.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValue) || 0), 0))}</span>
                <span className={`text-xs ${TONE_TEXT.ps} font-normal ml-2`}>P&S: R$ {fmt(wonLeads.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValuePS) || 0), 0))}</span>
              </h3>
              {wonLeads.length > 0 ? renderWonLostTable('Ganho', wonLeads) : emptyBlock('target', 'Nenhum lead ganho ainda')}
            </div>
          )}

          {/* Tab: Perdidos */}
          {activeTab === 'lost' && (
            <div className={`${SUPERFICIE} p-4`}>
              <h3 className='font-semibold text-white flex items-center gap-2 mb-3'>
                <span className='w-3 h-3 rounded-full bg-red-500'></span>
                Leads Perdidos ({lostLeadsFiltered.length})
                <span className='text-xs text-slate-500 font-normal ml-2'>MRR perdido: R$ {fmt(lostLeadsFiltered.reduce((s: number, l: any) => s + (parseFloat(l.estimatedValue) || 0), 0))}</span>
              </h3>
              <p className='text-xs text-central-texto-fraco -mt-2 mb-3' data-consequencia='perdido'>{CONSEQUENCIA.perdido}</p>
              {lostLeadsFiltered.length > 0 ? renderWonLostTable('Perdido', lostLeadsFiltered) : emptyBlock('trash', 'Nenhum lead perdido')}
            </div>
          )}

          {/* Tab: Congelados */}
          {activeTab === 'frozen' && (
            <div className={`${SUPERFICIE} p-4`}>
              <h3 className='font-semibold text-white flex items-center gap-2 mb-3'>
                <span className='w-3 h-3 rounded-full bg-cyan-500'></span>
                Leads Congelados ({frozenLeadsFiltered.length})
              </h3>
              <p className='text-xs text-central-texto-fraco -mt-2 mb-3' data-consequencia='congelado'>{CONSEQUENCIA.congelado}</p>
              {frozenLeadsFiltered.length > 0 ? renderFrozenTable(frozenLeadsFiltered) : emptyBlock('clock', 'Nenhum lead congelado')}
            </div>
          )}

          {/* Tab: Arquivados */}
          {activeTab === 'archived' && (
            <div className={`${SUPERFICIE} p-4`}>
              <h3 className='font-semibold text-white flex items-center gap-2 mb-3'>
                <span className='w-3 h-3 rounded-full bg-slate-500'></span>
                Leads Arquivados ({archivedLeadsFiltered.length})
              </h3>
              {archivedLeadsFiltered.length > 0 ? renderArchivedTable(archivedLeadsFiltered) : emptyBlock('clipboard', 'Nenhum lead arquivado')}
            </div>
          )}
        </EstadoDaLista>
      )}

      {/* DOCK de arrasto */}
      {isDragging && canEdit && (
        <div className='fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-3'>
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-emerald-400', 'scale-105'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-emerald-400', 'scale-105'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('ring-2', 'ring-emerald-400', 'scale-105');
              const leadId = e.dataTransfer.getData('leadId');
              setIsDragging(false);
              if (leadId) onStatusChange(parseInt(leadId), 'Ganho');
            }}
            className='px-6 py-4 rounded-xl bg-emerald-500/20 border-2 border-emerald-400/60 text-emerald-300 font-semibold text-sm backdrop-blur-md transition-all cursor-pointer'
          >
            ✅ Marcar Ganho
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-red-400', 'scale-105'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-red-400', 'scale-105'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('ring-2', 'ring-red-400', 'scale-105');
              const leadId = e.dataTransfer.getData('leadId');
              setIsDragging(false);
              if (leadId) {
                const lead = leads.find((l: any) => l.id === parseInt(leadId));
                if (lead) openLost(lead);
              }
            }}
            className='px-6 py-4 rounded-xl bg-red-500/20 border-2 border-red-400/60 text-red-300 font-semibold text-sm backdrop-blur-md transition-all cursor-pointer'
          >
            ❌ Marcar Perdido
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-cyan-400', 'scale-105'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-cyan-400', 'scale-105'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('ring-2', 'ring-cyan-400', 'scale-105');
              const leadId = e.dataTransfer.getData('leadId');
              setIsDragging(false);
              if (leadId) {
                const lead = leads.find((l: any) => l.id === parseInt(leadId));
                if (lead) openFreeze(lead);
              }
            }}
            className='px-6 py-4 rounded-xl bg-cyan-500/20 border-2 border-cyan-400/60 text-cyan-300 font-semibold text-sm backdrop-blur-md transition-all cursor-pointer'
          >
            ❄️ Congelar
          </div>
        </div>
      )}

      {/* Modal Congelar */}
      {freezeModal && (
        // Motivo e data digitados: antes fechava ao clicar fora; agora só pelo × ou
        // Cancelar. O ciano da primária vira o laranja.
        <CascaDeModal
          largura='xs'
          titulo='❄️ Congelar Lead'
          subtitulo={freezeModal.tradeName || freezeModal.companyName}
          onClose={() => setFreezeModal(null)}
          corpoClassName='p-6 space-y-4'
          rodape={
            <div className='ml-auto flex gap-2'>
              <button onClick={() => setFreezeModal(null)} className={`px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Cancelar</button>
              <button onClick={confirmFreeze} className={`px-4 py-2 rounded-lg text-sm ${acaoPrincipal}`}>Congelar</button>
            </div>
          }
        >
              <div>
                <label className='block text-xs font-medium text-slate-400 mb-1'>Motivo (opcional)</label>
                <input
                  type='text'
                  value={freezeForm.frozenReason}
                  onChange={e => setFreezeForm(f => ({ ...f, frozenReason: e.target.value }))}
                  placeholder='Ex.: cliente pediu para retomar em Q3'
                  className={`w-full px-3 py-2 placeholder-slate-500 rounded-lg text-sm ${campo}`}
                />
              </div>
              <div>
                <label className='block text-xs font-medium text-slate-400 mb-1'>Descongelar em (opcional)</label>
                <input
                  type='date'
                  value={freezeForm.frozenUntil}
                  onChange={e => setFreezeForm(f => ({ ...f, frozenUntil: e.target.value }))}
                  className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                />
                <p className='text-[11px] text-slate-500 mt-1'>Deixe vazio pra congelar sem prazo — descongela manualmente.</p>
              </div>
        </CascaDeModal>
      )}

      {/* Modal Motivo da Perda */}
      {lostModal && (
        // Motivo digitado: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        // "Marcar Perdido" segue vermelho (tira o lead do funil).
        <CascaDeModal
          largura='xs'
          titulo='❌ Marcar como Perdido'
          subtitulo={lostModal.tradeName || lostModal.companyName}
          onClose={() => setLostModal(null)}
          corpoClassName='p-6'
          rodape={
            <div className='ml-auto flex gap-2'>
              <button onClick={() => setLostModal(null)} className={`px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Cancelar</button>
              <button onClick={confirmLost} disabled={!lostReason.trim()} className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed ${BOTAO_PERIGO}`}>Marcar Perdido</button>
            </div>
          }
        >
              <label className='block text-xs font-medium text-slate-400 mb-1'>Motivo da perda *</label>
              <textarea
                value={lostReason}
                onChange={e => setLostReason(e.target.value)}
                rows={3}
                placeholder='Ex.: preço, concorrência, timing, sem orçamento…'
                className='w-full px-3 py-2 bg-slate-900 border border-slate-700 text-white placeholder-slate-500 rounded-lg text-sm focus:border-red-500'
                autoFocus
              />
        </CascaDeModal>
      )}

    </div>
  );
};

export default LeadsBoard;
